/**
 * Knowledge Sources Route
 * Lists workspace-wide knowledge sources
 */

import { TopBarRoute, ListView } from "../../components/templates";
import { Stack, Title, Card, Icon, Drawer } from "@introist/react-foundation/v2";
import { useKnowledgeSourcesWorkspace } from "../hooks/use-knowledge-sources-workspace";
import { Skeletons } from "../../components/molecules";
import { List, ListRow } from "../../components/organisms";
import { getKnowledgeSourceIcon } from "../components/KnowledgeSourceIcon";
import { useParams, useNavigate } from "react-router-dom";
import { KnowledgeSourceEditor } from "../components/KnowledgeSourceEditor";

export const KnowledgeSourcesRoute = () => {
  const { knowledgeSourceId } = useParams<{ knowledgeSourceId?: string }>();
  const navigate = useNavigate();

  return (
    <TopBarRoute
      paths={[
        {
          key: "knowledge-sources",
          title: "Knowledge Sources"
        }
      ]}
    >
      <Stack vertical>
        <ListView.Header title="Knowledge Sources" />
        <KnowledgeSourcesListContent />
      </Stack>

      {/* Knowledge Source Editor Drawer */}
      <Drawer open={!!knowledgeSourceId} onClose={() => navigate("/aistudio/knowledge")} width="640px">
        {knowledgeSourceId && (
          <KnowledgeSourceEditor
            knowledgeSourceId={knowledgeSourceId}
            onClose={() => navigate("/aistudio/knowledge")}
          />
        )}
      </Drawer>
    </TopBarRoute>
  );
};

const KnowledgeSourcesListContent = () => {
  const { data: knowledgeSources = [], isLoading } = useKnowledgeSourcesWorkspace();
  const navigate = useNavigate();

  if (isLoading) {
    return <Skeletons.List rowCount={3} rowHeight={60} />;
  }

  if (knowledgeSources.length === 0) {
    return (
      <Card>
        <Stack
          vertical
          style={{
            alignItems: "center",
            gap: "var(--spacing-small)",
            padding: "var(--spacing-large)"
          }}
        >
          <Icon name="folder" dimmed />
          <Title style={{ color: "var(--palette-foreground-subdued)" }}>
            No knowledge sources configured yet
          </Title>
          <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
            Knowledge sources provide context for agents
          </Title>
        </Stack>
      </Card>
    );
  }

  return (
    <List>
      {knowledgeSources.map(source => (
        <ListRow
          key={source.id}
          gridTemplateColumns="auto 1fr"
          onClick={() => navigate(`/aistudio/knowledge/${source.id}`)}
          clickable
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {getKnowledgeSourceIcon(source.type)}
          </div>
          <Title variant="bold">{source.name}</Title>
        </ListRow>
      ))}
    </List>
  );
};
